import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AvailabilitySlotsPicker } from '@/components/AvailabilitySlotsPicker';
import { CUISINES } from '@/data/cuisines';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES = 5;
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1.2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.85,
  fileType: 'image/jpeg'
};

const categories = ['Hot Meals', 'Baked Goods', 'Desserts', 'Sauces & Preserves', 'Meal Prep'];
const CUSTOM_CATEGORY_VALUE = '__custom__';
const dietaryOptions = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Halal', 'Nut-Free', 'Dairy-Free'];
const fulfillmentOptions = ['Pickup', 'Delivery', 'Both'];

export const EditListingPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [customCategoryMode, setCustomCategoryMode] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ingredients: '',
    dietary_tags: [],
    price: '',
    quantity: '',
    fulfillment_options: [],
    availability_dates: [],
    category: '',
    cuisine: '',
    location: ''
  });

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      if (user && data.seller_id !== user.id) {
        toast.error('You can only edit your own listings');
        navigate('/my-listings');
        return;
      }
      setFormData({
        title: data.title || '',
        description: data.description || '',
        ingredients: data.ingredients || '',
        dietary_tags: data.dietary_tags || [],
        price: String(data.price ?? ''),
        quantity: String(data.quantity ?? ''),
        fulfillment_options: data.fulfillment_options || [],
        availability_dates: data.availability_dates || [],
        category: data.category || '',
        cuisine: data.cuisine || '',
        location: data.location || ''
      });
      // If loaded category isn't one of the presets, switch to custom mode so the input renders
      if (data.category && !categories.includes(data.category)) {
        setCustomCategoryMode(true);
      }
      setExistingImages(data.images || []);
    } catch (error) {
      toast.error('Listing not found');
      navigate('/my-listings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckbox = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(item => item !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 0) return;

    const currentTotal = existingImages.length + newFiles.length;
    const remainingSlots = MAX_IMAGES - currentTotal;
    if (remainingSlots <= 0) {
      toast.error(`You can have a maximum of ${MAX_IMAGES} photos`);
      return;
    }
    const filesToProcess = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.warning(`Only the first ${remainingSlots} photo(s) will be added (max ${MAX_IMAGES} total)`);
    }

    setUploading(true);
    try {
      const results = await Promise.all(filesToProcess.map(async (file) => {
        let toUpload = file;
        try {
          const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
          toUpload = compressed.size < file.size ? compressed : file;
        } catch (compressionError) {
          console.warn('Compression failed, uploading original:', compressionError);
        }
        const fd = new FormData();
        fd.append('file', toUpload, toUpload.name || file.name);
        const { data } = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return data.url;
      }));
      setNewFiles((prev) => [...prev, ...results]);
      toast.success(`${results.length} photo${results.length > 1 ? 's' : ''} uploaded`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload';
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const removeExistingImage = (url) => {
    setExistingImages(existingImages.filter(u => u !== url));
  };

  const removeNewFile = (url) => {
    setNewFiles(newFiles.filter(u => u !== url));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.category.trim()) {
      toast.error('Title, description, and category are required');
      return;
    }
    setSaving(true);
    try {
      const allImages = [...existingImages, ...newFiles];
      await axios.put(
        `${API}/listings/${id}`,
        {
          title: formData.title,
          description: formData.description,
          ingredients: formData.ingredients,
          dietary_tags: formData.dietary_tags,
          price: parseFloat(formData.price),
          quantity: parseInt(formData.quantity),
          fulfillment_options: formData.fulfillment_options,
          availability_dates: formData.availability_dates,
          category: formData.category,
          cuisine: formData.cuisine || null,
          location: formData.location,
          images: allImages
        },
        { withCredentials: true }
      );
      toast.success('Listing updated');
      navigate(`/listing/${id}`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update';
      toast.error(typeof detail === 'string' ? detail : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F6F0' }}>
        <p style={{ fontFamily: 'Manrope' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl sm:text-5xl mb-8 tracking-tight"
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          data-testid="edit-listing-heading"
        >
          Edit Listing
        </h1>

        <div className="p-8 rounded-md border border-[#E5E0D8] space-y-6" style={{ backgroundColor: '#FFFFFF' }} data-testid="edit-listing-form">
          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Title *</label>
            <input
              type="text" name="title" value={formData.title} onChange={handleChange}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="title-input"
            />
          </div>

          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Category *</label>
            <select
              name="category"
              value={customCategoryMode ? CUSTOM_CATEGORY_VALUE : formData.category}
              onChange={(e) => {
                const v = e.target.value;
                if (v === CUSTOM_CATEGORY_VALUE) {
                  setCustomCategoryMode(true);
                  setFormData({ ...formData, category: '' });
                } else {
                  setCustomCategoryMode(false);
                  setFormData({ ...formData, category: v });
                }
              }}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="category-select"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              <option value={CUSTOM_CATEGORY_VALUE}>Type your own…</option>
            </select>
            {customCategoryMode && (
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Sushi Rolls, Fermented Foods, BBQ Platters"
                maxLength={60}
                autoFocus
                className="w-full mt-2 px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="category-custom-input"
              />
            )}
          </div>

          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Type of Cuisine</label>
            <select
              name="cuisine" value={formData.cuisine} onChange={handleChange}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="cuisine-select"
            >
              <option value="">Select a cuisine (optional)</option>
              {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Description *</label>
            <textarea
              name="description" value={formData.description} onChange={handleChange} rows={4}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="description-input"
            />
          </div>

          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Ingredients</label>
            <textarea
              name="ingredients" value={formData.ingredients} onChange={handleChange} rows={3}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="ingredients-input"
            />
          </div>

          <div>
            <label className="block mb-3" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Dietary Tags</label>
            <div className="flex flex-wrap gap-3">
              {dietaryOptions.map((diet) => (
                <label key={diet} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.dietary_tags.includes(diet)}
                    onChange={() => handleCheckbox('dietary_tags', diet)}
                    className="w-4 h-4"
                    data-testid={`dietary-tag-${diet.toLowerCase()}`}
                  />
                  <span style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{diet}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Price ($) *</label>
              <input
                type="number" name="price" value={formData.price} onChange={handleChange} min="0" step="0.01"
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="price-input"
              />
            </div>
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Quantity *</label>
              <input
                type="number" name="quantity" value={formData.quantity} onChange={handleChange} min="0"
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="quantity-input"
              />
              <p className="mt-1" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                Setting quantity to 0 will hide the listing from public browsing
              </p>
            </div>
          </div>

          <div>
            <label className="block mb-3" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Fulfillment Options</label>
            <div className="flex flex-wrap gap-3">
              {fulfillmentOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.fulfillment_options.includes(option)}
                    onChange={() => handleCheckbox('fulfillment_options', option)}
                    className="w-4 h-4"
                    data-testid={`fulfillment-${option.toLowerCase()}`}
                  />
                  <span style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Location *</label>
            <input
              type="text" name="location" value={formData.location} onChange={handleChange}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="location-input"
            />
          </div>

          <AvailabilitySlotsPicker
            value={formData.availability_dates}
            onChange={(slots) => setFormData({ ...formData, availability_dates: slots })}
          />

          <div>
            <div className="flex items-baseline justify-between mb-3">
              <label style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>Photos</label>
              <span
                style={{ fontFamily: 'Manrope', fontSize: '12px', color: (existingImages.length + newFiles.length) >= MAX_IMAGES ? '#D95A41' : '#5C5C5C' }}
                data-testid="image-count"
              >
                {existingImages.length + newFiles.length} / {MAX_IMAGES}
              </span>
            </div>
            <div
              className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${
                (existingImages.length + newFiles.length) >= MAX_IMAGES
                  ? 'border-[#E5E0D8] opacity-60 cursor-not-allowed'
                  : 'border-[#E5E0D8] cursor-pointer hover:border-[#D95A41]'
              }`}
              onClick={() => (existingImages.length + newFiles.length) < MAX_IMAGES && document.getElementById('edit-file-upload').click()}
              data-testid="image-upload-area"
            >
              <Upload size={32} className="mx-auto mb-2" style={{ color: '#5C5C5C', strokeWidth: 1.5 }} />
              <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px' }}>
                {uploading
                  ? 'Uploading...'
                  : (existingImages.length + newFiles.length) >= MAX_IMAGES
                    ? 'Photo limit reached — remove one to add more'
                    : 'Click to upload more images'}
              </p>
              <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C', marginTop: '4px' }}>
                Up to {MAX_IMAGES} photos · auto-compressed
              </p>
              <input
                id="edit-file-upload"
                type="file"
                accept="image/*"
                multiple
                disabled={(existingImages.length + newFiles.length) >= MAX_IMAGES}
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {(existingImages.length > 0 || newFiles.length > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {existingImages.map((url, idx) => (
                  <div key={`e-${idx}`} className="relative aspect-square rounded-md overflow-hidden border border-[#E5E0D8]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button" onClick={() => removeExistingImage(url)}
                      className="absolute top-2 right-2 p-1 rounded-full"
                      style={{ backgroundColor: 'rgba(26,26,26,0.8)' }}
                      data-testid={`remove-existing-image-${idx}`}
                    >
                      <X size={16} style={{ color: '#FFFFFF' }} />
                    </button>
                  </div>
                ))}
                {newFiles.map((url, idx) => (
                  <div key={`n-${idx}`} className="relative aspect-square rounded-md overflow-hidden border border-[#E5E0D8]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button" onClick={() => removeNewFile(url)}
                      className="absolute top-2 right-2 p-1 rounded-full"
                      style={{ backgroundColor: 'rgba(26,26,26,0.8)' }}
                      data-testid={`remove-new-image-${idx}`}
                    >
                      <X size={16} style={{ color: '#FFFFFF' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-6 border-t border-[#E5E0D8]">
            <Button
              onClick={handleSave} disabled={saving}
              className="rounded-md"
              style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="save-listing-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={() => navigate('/my-listings')}
              variant="ghost"
              style={{ fontFamily: 'Manrope' }}
              data-testid="cancel-edit-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
